import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import {
    PutStateRequestBody,
    fetchGetStateByStateKey,
    fetchPutState
} from '@iwoca/lapi-client/edge';
import { Button, CheckBox, ErrorMessage, Text } from '@iwoca/orion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { NameFields } from './NameFields/NameFields';
import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';
import AddressFields from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/fields/AddressFields';
import DOBFields from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/fields/DOBFields';
import {
    convertDeserialisedPersonToFormValues,
    convertSuggestionToFormValues,
    createCustomerStateWithNewOrUpdatedPerson
} from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/MSHForm.helpers';
import {
    TFormValues,
    TPerson,
    TSuggestion
} from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/MSHForm.types';

type TShareholderFormProps = {
    onClose: () => void;
    personBeingEdited?: TPerson;
    suggestionBeingConfirmed?: TSuggestion;
};

export const ShareholderForm = ({
    onClose,
    personBeingEdited,
    suggestionBeingConfirmed
}: TShareholderFormProps) => {
    const intl = useIntl();
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });

    const getDefaultFormValues = () => {
        if (personBeingEdited) {
            return convertDeserialisedPersonToFormValues(personBeingEdited);
        }
        if (suggestionBeingConfirmed) {
            return convertSuggestionToFormValues(suggestionBeingConfirmed);
        }
        return { address: { postcode: '' } };
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
        setValue,
        trigger
    } = useForm<TFormValues>({ defaultValues: getDefaultFormValues() });

    useEffect(() => {
        if (suggestionBeingConfirmed) {
            trigger();
        }
    }, [suggestionBeingConfirmed, trigger]);

    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({ stateKey, body: state }),
        onSuccess: async () => {
            if (personBeingEdited) {
                tracking.ampli.fundingRequirementsShareholderEdited({
                    type: 'added',
                    version: '2.0'
                });
            } else if (suggestionBeingConfirmed) {
                tracking.ampli.fundingRequirementsShareholderEdited({
                    type: 'suggested',
                    version: '2.0'
                });
            } else {
                tracking.ampli.fundingRequirementsShareholderAdded({
                    version: '2.0'
                });
            }
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
            await queryClient.invalidateQueries({
                queryKey: ['fetchGetFundingRequirementsCreditFacility']
            });
            onClose();
        }
    });

    const onSubmit: SubmitHandler<TFormValues> = (values) => {
        if (!customerState) return;
        const newState = createCustomerStateWithNewOrUpdatedPerson(
            values,
            customerState
        );
        saveCustomerState.mutate(newState);
    };

    const isApplicantBeingEdited = personBeingEdited?.isApplicant ?? false;
    const isEditing = Boolean(personBeingEdited || suggestionBeingConfirmed);

    return (
        <div className="my-2xl">
            <div className="flex items-center justify-between">
                <Text className="font-bold">
                    {isEditing ? (
                        <FormattedMessage
                            id="_.shareholder_form.title_edit"
                            defaultMessage="Edit person"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.shareholder_form.title_add"
                            defaultMessage="Add a director or major shareholder"
                        />
                    )}
                </Text>
                <Button
                    variant="tertiary"
                    className="h-auto p-0 font-med underline"
                    size="small"
                    onClick={onClose}
                    type="button"
                >
                    <FormattedMessage
                        id="_.shareholder_form.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-l space-y-l">
                <div className="space-y-m">
                    <div className="flex gap-m">
                        <CheckBox
                            {...register('shareholder')}
                            id="shareholder"
                        />
                        {/* Using label tag instead of passing the label prop because the Checkbox component wraps the label internally  */}
                        <label htmlFor="shareholder" className="font-reg">
                            <FormattedMessage
                                id="_.shareholder_form.shareholder_checkbox_label"
                                defaultMessage="Major Shareholder (25%+ of shares or voting rights)"
                            />
                        </label>
                    </div>
                    <CheckBox
                        {...register('director')}
                        label={intl.formatMessage({
                            id: '_.shareholder_form.director_checkbox_label',
                            defaultMessage: 'Director'
                        })}
                        disabled={isApplicantBeingEdited}
                    />
                </div>

                <NameFields
                    errors={errors}
                    register={register}
                    disabled={Boolean(personBeingEdited)}
                />

                <DOBFields
                    errors={errors}
                    register={register}
                    disabled={Boolean(personBeingEdited)}
                    variant="new"
                />

                <AddressFields
                    errors={errors}
                    register={register}
                    getValues={getValues}
                    setValue={setValue}
                    disabled={isApplicantBeingEdited}
                    variant="new"
                />

                <Button
                    variant="primary-rounded"
                    type="submit"
                    loading={saveCustomerState.isPending}
                >
                    {isEditing ? (
                        <FormattedMessage
                            id="_.shareholder_form.confirm_save"
                            defaultMessage="Confirm and save"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.shareholder_form.confirm_add"
                            defaultMessage="Confirm and add"
                        />
                    )}
                </Button>

                {saveCustomerState.isError && (
                    <ErrorMessage className="mt-m">
                        <FormattedMessage
                            id="_.shareholder_form.submit_error"
                            defaultMessage="Sorry there was an error, please try again"
                        />
                    </ErrorMessage>
                )}
            </form>
        </div>
    );
};
